import { useAIAgents } from '@/hooks/useAIAgents';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreateAgentFromTemplateDialog } from './CreateAgentFromTemplateDialog';
import { useState } from 'react';
import { Brain, Users, TrendingUp, Download } from 'lucide-react';

export const AgentTemplates = () => {
  const { useAgentTemplates } = useAIAgents();
  const { data: templates = [], isLoading } = useAgentTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const getAgentTypeLabel = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getAgentTypeIcon = (type: string) => {
    switch (type) {
      case 'data_validator':
        return '✓';
      case 'anomaly_detector':
        return '🔍';
      case 'invoice_processor':
        return '📄';
      case 'cost_optimizer':
        return '💰';
      case 'report_generator':
        return '📊';
      case 'collection_manager':
        return '📞';
      default:
        return '🤖';
    }
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) => (
          <Card key={template.id} className="relative group hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <div className="text-2xl">{getAgentTypeIcon(template.agent_type)}</div>
                  <div>
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <CardDescription className="text-sm">
                      {getAgentTypeLabel(template.agent_type)}
                    </CardDescription>
                  </div>
                </div>
                {template.is_public && (
                  <Badge variant="secondary" className="text-xs">
                    Public
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {template.description && (
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {template.description}
                </p>
              )}
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-muted-foreground">
                  <Users className="mr-1 h-3 w-3" />
                  {template.usage_count} uses
                </div>
                <div className="text-muted-foreground">
                  {template.model_config?.provider || 'OpenAI'}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">Features:</div>
                <div className="flex flex-wrap gap-1">
                  {Object.keys(template.configuration).slice(0, 3).map((feature) => (
                    <Badge key={feature} variant="outline" className="text-xs">
                      {feature.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                  {Object.keys(template.configuration).length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{Object.keys(template.configuration).length - 3} more
                    </Badge>
                  )}
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={() => setSelectedTemplate(template.id)}
              >
                <Download className="mr-2 h-4 w-4" />
                Use Template
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {templates.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Brain className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No templates available</h3>
          <p className="text-muted-foreground">
            Templates will appear here when they become available.
          </p>
        </div>
      )}

      {selectedTemplate && (
        <CreateAgentFromTemplateDialog
          templateId={selectedTemplate}
          open={true}
          onOpenChange={(open) => !open && setSelectedTemplate(null)}
        />
      )}
    </>
  );
};